import type { TradingVpsRow } from '../types/api';

export type GuardStatus = TradingVpsRow['status'];

export type TradingVpsStateKey = 'live' | 'delayed' | 'offline' | 'mt5_down' | 'broker_off' | 'algo_off';

// Heartbeat freshness thresholds in seconds. These MUST mirror the backend's
// backend/src/guard/status.ts (the source of truth): live <= 5s, delayed <= 15s,
// offline > 15s. The guard beats once per second, so these tolerate one or two late
// heartbeats without flapping the fleet band.
export const GUARD_LIVE_MAX_AGE_S = 5;
export const GUARD_DELAYED_MAX_AGE_S = 15;

/** Poll-data age (seconds) beyond which the trading-VPS feed itself counts as stale. */
export const TRADING_VPS_STALE_AFTER_S = 5;

const STATUS_RANK: Record<GuardStatus, number> = { live: 0, delayed: 1, offline: 2 };

export interface TradingVpsState {
  key: TradingVpsStateKey;
  label: string;
  issue: boolean;
  rank: number;
}

export interface TradingVpsSummary {
  total: number;
  live: number;
  issues: number;
  message: string;
}

/** Client-side mirror of the server's guardStatus(ageSeconds) classification. */
export function guardStatusFromAge(ageSeconds: number | null): GuardStatus {
  if (ageSeconds == null || !Number.isFinite(ageSeconds)) return 'offline';
  if (ageSeconds <= GUARD_LIVE_MAX_AGE_S) return 'live';
  if (ageSeconds <= GUARD_DELAYED_MAX_AGE_S) return 'delayed';
  return 'offline';
}

/**
 * Effective heartbeat status once the polled data itself is `dataAgeS` seconds old: the
 * server-computed status can only be DOWNGRADED (never upgraded) as the row keeps aging
 * client-side. This is what prevents frozen LIVE badges when the browser cannot reach the
 * monitoring server — the rows keep aging toward delayed/offline on screen.
 */
export function effectiveGuardStatus(row: TradingVpsRow, dataAgeS = 0): GuardStatus {
  if (dataAgeS <= 0 || row.age_s == null) return row.status;
  const aged = guardStatusFromAge(row.age_s + dataAgeS);
  return STATUS_RANK[aged] > STATUS_RANK[row.status] ? aged : row.status;
}

/** Stale-feed age used for status degradation: 0 until the feed is genuinely stale. */
export function tradingVpsStaleAgeS(dataAgeS: number): number {
  return dataAgeS > TRADING_VPS_STALE_AFTER_S ? dataAgeS : 0;
}

export function groupTradingVps(rows: TradingVpsRow[], dataAgeS = 0): TradingVpsRow[] {
  const hosts = new Map<string, TradingVpsRow>();
  for (const row of rows) {
    const current = hosts.get(row.vps_id);
    if (!current || deriveTradingVpsState(row, dataAgeS).rank < deriveTradingVpsState(current, dataAgeS).rank) {
      hosts.set(row.vps_id, row);
    }
  }
  return sortTradingVps([...hosts.values()], dataAgeS);
}

export function deriveTradingVpsState(row: TradingVpsRow, dataAgeS = 0): TradingVpsState {
  const status = effectiveGuardStatus(row, dataAgeS);
  if (status === 'offline') return { key: 'offline', label: 'OFFLINE', issue: true, rank: 0 };
  if (status === 'delayed') return { key: 'delayed', label: 'DELAYED', issue: true, rank: 2 };
  if (!row.terminal_reachable) return { key: 'mt5_down', label: 'MT5 DOWN', issue: true, rank: 0 };
  if (row.connected === false) return { key: 'broker_off', label: 'BROKER OFF', issue: true, rank: 1 };
  if (row.trade_expert === false) return { key: 'algo_off', label: 'ALGO OFF', issue: true, rank: 1 };
  return { key: 'live', label: 'LIVE', issue: false, rank: 3 };
}

export function summarizeTradingVps(rows: TradingVpsRow[], dataAgeS = 0): TradingVpsSummary {
  const hosts = groupTradingVps(rows, dataAgeS);
  const total = hosts.length;
  const live = hosts.filter((row) => !deriveTradingVpsState(row, dataAgeS).issue).length;
  const issues = total - live;
  let message = 'No execution hosts reporting';
  if (total > 0 && issues === 0) message = 'All execution hosts reporting';
  if (issues === 1) message = '1 execution host needs attention';
  if (issues > 1) message = `${issues} execution hosts need attention`;
  return { total, live, issues, message };
}

export function sortTradingVps(rows: TradingVpsRow[], dataAgeS = 0): TradingVpsRow[] {
  return rows.slice().sort((a, b) => {
    const stateDelta = deriveTradingVpsState(a, dataAgeS).rank - deriveTradingVpsState(b, dataAgeS).rank;
    return stateDelta || a.vps_id.localeCompare(b.vps_id) || a.terminal_key.localeCompare(b.terminal_key);
  });
}

// ---- Flap damping ---------------------------------------------------------------------
// A single missed poll must not flash a host red: a WORSE heartbeat status is applied only
// when two consecutive polls agree the host degraded. An IMPROVING status applies
// immediately (good news is never delayed). Only the heartbeat freshness field is damped —
// the terminal booleans (MT5 down / broker off / algo off) come from real heartbeat
// payloads and are shown as reported.

export interface GuardStatusDampingEntry {
  /** The status currently shown for this row. */
  shown: GuardStatus;
  /** Whether the PREVIOUS poll already reported a status worse than `shown`. */
  sawWorse: boolean;
}

export type GuardStatusDampingLedger = Map<string, GuardStatusDampingEntry>;

function dampingKey(row: TradingVpsRow): string {
  return `${row.vps_id}:${row.terminal_key}`;
}

/**
 * Apply per-row status damping to one poll result. Mutates `ledger` (the caller owns it for
 * the lifetime of the poll loop) and returns the rows to display. Rows for hosts that
 * disappeared from the response are pruned from the ledger.
 */
export function dampenTradingVpsStatuses(
  rows: TradingVpsRow[],
  ledger: GuardStatusDampingLedger,
): TradingVpsRow[] {
  const seen = new Set<string>();
  const result = rows.map((row) => {
    const key = dampingKey(row);
    seen.add(key);
    const entry = ledger.get(key);
    if (!entry) {
      // First sighting: show as reported.
      ledger.set(key, { shown: row.status, sawWorse: false });
      return row;
    }
    if (STATUS_RANK[row.status] <= STATUS_RANK[entry.shown]) {
      // Same or improving: apply immediately.
      entry.shown = row.status;
      entry.sawWorse = false;
      return row;
    }
    if (entry.sawWorse) {
      // Second consecutive degraded poll: the downgrade is real — apply it.
      entry.shown = row.status;
      entry.sawWorse = false;
      return row;
    }
    // First degraded poll: hold the last shown status for one more cycle.
    entry.sawWorse = true;
    return { ...row, status: entry.shown };
  });
  for (const key of [...ledger.keys()]) {
    if (!seen.has(key)) ledger.delete(key);
  }
  return result;
}
